import { useEffect, useState } from "react";
import { Mic, Sparkles, TrendingUp } from "lucide-react";

/**
 * HeroLiveWaveform — the animated "Mental wellbeing check-in" mock card
 * from the ManoVāk landing hero. Pure CSS/JS animation loop; does not
 * request microphone permission. Counts up 00:00 → 00:30 → resets.
 */
const BAR_COUNT = 36;
// Precomputed seed heights (0.2 - 1.0) so bars feel organic + repeat cleanly
const SEED = Array.from({ length: BAR_COUNT }, (_, i) => {
  const wave = Math.sin(i * 0.4) * 0.4 + Math.cos(i * 0.9) * 0.25 + 0.55;
  return Math.max(0.22, Math.min(1, wave));
});

function fmt(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function HeroLiveWaveform() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((v) => (v + 1) % 31), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="hero-live-waveform"
      className="relative w-full max-w-[560px] mx-auto"
    >
      {/* Latest check-in floating card (top right) */}
      <div className="absolute -top-4 right-0 z-20 hidden sm:flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-4 py-3 border border-[color:var(--mano-border)] shadow-sm">
        <div className="h-9 w-9 rounded-xl bg-[color:var(--mano-blue-light)] flex items-center justify-center text-[color:var(--mano-primary)]">
          <Sparkles size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--mano-ink-soft)] font-semibold">
            Latest check-in <span className="text-[color:var(--mano-ink-soft)]/70">· 2 days ago</span>
          </div>
          <div className="text-sm font-semibold text-[color:var(--mano-ink)]">
            Mental wellbeing{" "}
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-[color:var(--mano-normal)]/12 text-[color:var(--mano-normal)] px-2 py-0.5 text-[11px] font-bold">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--mano-normal)]" />
              Normal
            </span>
          </div>
          <div className="text-[11px] text-[color:var(--mano-ink-soft)]">Within expected range</div>
        </div>
      </div>

      {/* Main check-in card */}
      <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6 sm:p-7 shadow-[0_20px_60px_-30px_rgba(10,39,69,0.35)]">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[color:var(--mano-primary)]">
          Mental wellbeing check-in
        </div>
        <h3 className="mt-2 font-display text-2xl sm:text-[26px] leading-tight text-[color:var(--mano-ink)]">
          Speak naturally for a moment.
        </h3>
        <div className="mt-1 text-[11px] text-[color:var(--mano-ink-soft)]">Step 1 of 2</div>

        <div className="mt-5 rounded-2xl bg-[color:var(--mano-blue-light)]/60 p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[color:var(--mano-ink-soft)] mb-2">
            Guided prompt
          </div>
          <p className="text-sm sm:text-[15px] text-[color:var(--mano-ink)] leading-relaxed">
            &ldquo;How have you been feeling in the past few days?&rdquo;
          </p>
        </div>

        {/* Waveform bars */}
        <div className="mt-5 flex items-center justify-center gap-[3px] h-16">
          {SEED.map((h, i) => (
            <span
              key={i}
              className="hero-wave-bar"
              style={{
                height: `${Math.round(h * 100)}%`,
                animationDelay: `${(i * 47) % 900}ms`,
                animationDuration: `${800 + (i * 37) % 400}ms`,
                opacity: 0.85,
              }}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-[13px]">
          <div className="font-mono tabular-nums text-[color:var(--mano-ink)]">
            <span className="font-bold">{fmt(t)}</span>
            <span className="text-[color:var(--mano-ink-soft)]"> / 00:30</span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[color:var(--mano-high)] font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--mano-high)] opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[color:var(--mano-high)]" />
            </span>
            Recording…
          </div>
        </div>

        {/* Center mic badge */}
        <div className="mt-5 flex items-center justify-center">
          <div className="h-11 w-11 rounded-full bg-[color:var(--mano-primary)] text-white flex items-center justify-center shadow-lg">
            <Mic size={18} />
          </div>
        </div>
      </div>

      {/* Progress sparkline card (bottom right) */}
      <div className="absolute -bottom-6 -right-2 z-20 hidden md:block rounded-2xl bg-white border border-[color:var(--mano-border)] p-4 w-[240px] shadow-sm">
        <div className="flex items-center justify-between text-[11px] text-[color:var(--mano-ink-soft)]">
          <span className="uppercase tracking-widest font-semibold">Your progress</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--mano-blue-light)] text-[color:var(--mano-primary)] px-2 py-0.5 font-bold">
            <TrendingUp size={10} /> Mental
          </span>
        </div>
        <svg viewBox="0 0 200 60" className="mt-2 w-full">
          <path
            d="M0,45 C 30,40 40,20 60,25 S 100,42 130,30 S 175,15 200,20"
            fill="none"
            stroke="var(--mano-primary)"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <circle cx="200" cy="20" r="3.5" fill="var(--mano-primary)" />
        </svg>
        <div className="flex justify-between text-[9px] text-[color:var(--mano-ink-soft)] uppercase tracking-wider mt-1">
          <span>High</span><span>Expected</span><span>Low</span>
        </div>
      </div>
    </div>
  );
}
